import { useMutation } from '@apollo/client';
import { IonProgressBar } from '@ionic/react';
import { InputHTMLAttributes, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Control, Controller, FieldError, FieldPath, FieldValues } from 'react-hook-form';
import { useIntl } from 'react-intl';

import ActionIconButton from '@components/buttons/ActionIconButton';
import Error from '@components/fields/Error';
import styles from '@components/fields/MessageDirectUploadFileInput.module.css';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import {
  DirectUploadCreateDocument,
  DirectUploadCreateMutation,
  DirectUploadCreateMutationVariables
} from '@typing/Generated';
import { computeChecksumMd5, directUpload } from '@utils/fileUtils';
import { clickCurrentTarget } from '@utils/keyboardUtils';

type DirectUpload = NonNullable<NonNullable<DirectUploadCreateMutation['directUploadCreate']>['directUpload']>;

export type DirectUploadAndFile = {
  directUpload: DirectUpload;
  file: File;
};

type UploadProps = {
  onDelete: (uploadAndFile: DirectUploadAndFile) => void;
  onUploadComplete: (uploadAndFile: DirectUploadAndFile) => void;
  uploadAndFile: DirectUploadAndFile;
};

export const Upload = ({ onDelete, onUploadComplete, uploadAndFile }: UploadProps) => {
  const [progress, setProgress] = useState<number>(0);

  useEffect(() => {
    directUpload({
      file: uploadAndFile.file,
      jsonEncodedHeaders: uploadAndFile.directUpload.headers,
      onProgress: percentComplete => {
        setProgress(percentComplete);
      },
      url: uploadAndFile.directUpload.url
    }).then(() => {
      onUploadComplete(uploadAndFile);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = useCallback(() => {
    onDelete(uploadAndFile);
  }, [onDelete, uploadAndFile]);

  return (
    <div className={styles.uploadCard}>
      <div className={styles.breakWord}>
        <div className="ion-margin-bottom">
          <BodyCopyBold color="shadesWhite"> {uploadAndFile.file.name}</BodyCopyBold>
        </div>
        <IonProgressBar className={styles.progressBar} value={progress ? progress / 100.0 : 0.0} />
      </div>

      <div>
        <ActionIconButton
          ariaLabelKey="accessibility.ariaLabels.delete"
          color="shadesWhite"
          icon="times"
          onClick={handleDelete}
          size="medium"
        />
      </div>
    </div>
  );
};

type Props<FormData extends FieldValues> = InputHTMLAttributes<HTMLInputElement> & {
  busyFiles: React.MutableRefObject<File[]>;
  clearFileTypeError?: () => void;
  control: Control<FormData>;
  directUploadAndFiles: DirectUploadAndFile[];
  error: FieldError | undefined;
  imageRequired?: boolean;
  label?: string | ReactNode;
  largeDownloadWidth: boolean;
  name: FieldPath<FormData>;
  onBusyChange: (busy: boolean) => void;
  required?: boolean;
  setDirectUploadAndFiles: (files: DirectUploadAndFile[]) => void;
  setFileTypeError?: () => void;
  setValue: (value: string[] | undefined) => void;
};

const MessageDirectUploadFileInput = <FormData extends FieldValues>({
  accept,
  busyFiles,
  clearFileTypeError,
  control,
  directUploadAndFiles,
  disabled,
  error,
  label,
  largeDownloadWidth,
  name,
  onBusyChange,
  required,
  setDirectUploadAndFiles,
  setFileTypeError,
  setValue,
  value,
  ...inputProps
}: Props<FormData>) => {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);

  const [createDirectUpload] = useMutation<DirectUploadCreateMutation, DirectUploadCreateMutationVariables>(
    DirectUploadCreateDocument
  );

  const handleStartUpload = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, [inputRef]);

  return (
    <div className={styles.container}>
      <div className={styles.wrapperItem}>
        <Controller
          control={control}
          name={name}
          render={({ field: { name, onBlur, onChange, value } }) => {
            const typedValue = value as string[] | null;
            if (typedValue?.length !== directUploadAndFiles.length && typedValue?.length === 0) {
              setDirectUploadAndFiles([]);
              busyFiles.current = [];
            }

            return (
              <>
                {/* eslint-disable-next-line react/forbid-elements */}
                <input
                  accept={accept ? accept : '*'}
                  aria-label={intl.formatMessage({
                    id: 'accessibility.ariaLabels.attachFiles'
                  })}
                  className={styles.input}
                  multiple
                  name={name}
                  onBlur={onBlur}
                  onChange={e => {
                    const files = e?.target?.files;
                    if (files) {
                      const createDirectUploadPromises: Promise<DirectUploadAndFile>[] = [];
                      onBusyChange(true);
                      const newBusyFiles: File[] = [...busyFiles.current];

                      for (let index = 0; index < files.length ?? 0; ++index) {
                        const file = e?.target?.files?.[index] as File;
                        if (clearFileTypeError) {
                          clearFileTypeError();
                        }
                        newBusyFiles.push(file);
                        createDirectUploadPromises.push(
                          new Promise((resolve, reject) => {
                            computeChecksumMd5(file).then(checksum => {
                              createDirectUpload({
                                variables: {
                                  byteSize: file.size,
                                  checksum,
                                  contentType: file.type,
                                  filename: file.name
                                }
                              }).then(response => {
                                const directUpload = response.data?.directUploadCreate?.directUpload;
                                if (directUpload) {
                                  resolve({ directUpload, file });
                                } else {
                                  reject('NO BLOB ID');
                                }
                              });
                            });
                          })
                        );
                        busyFiles.current = newBusyFiles;

                        Promise.all<DirectUploadAndFile>(createDirectUploadPromises).then(result => {
                          const existing = directUploadAndFiles;
                          const newDirectUploadAndFiles = [...existing, ...result];
                          onChange(newDirectUploadAndFiles.map(upload => upload.directUpload.signedBlobId));

                          setDirectUploadAndFiles(newDirectUploadAndFiles);
                        });
                      }
                    }
                  }}
                  ref={inputRef}
                  type="file"
                  {...inputProps}
                />
                <div
                  className={styles.fakeInputWrapper}
                  onClick={disabled ? undefined : handleStartUpload}
                  onKeyUp={clickCurrentTarget}
                  tabIndex={inputProps.tabIndex}
                >
                  {directUploadAndFiles.length > 0 ? (
                    <ActionIconButton
                      ariaLabelKey="forms.directUploadFileInput.attachMoreFiles"
                      color="neutral500"
                      disabled={disabled}
                      hoverContent={intl.formatMessage({ id: 'forms.directUploadFileInput.attachMoreFiles' })}
                      icon="paperclip"
                    />
                  ) : (
                    <ActionIconButton
                      ariaLabelKey="forms.directUploadFileInput.attachFiles"
                      color="neutral500"
                      disabled={disabled}
                      hoverContent={intl.formatMessage({ id: 'forms.directUploadFileInput.attachFiles' })}
                      icon="paperclip"
                    />
                  )}
                </div>
              </>
            );
          }}
        />
      </div>
      {error && (
        <div className={styles.errorWrapper}>
          <Error error={error} />
        </div>
      )}
    </div>
  );
};

export default MessageDirectUploadFileInput;
